'use client';

import { useAtomValue } from 'jotai';

import { audioQueueAtom } from '@/shared-survey';
import { currentPlayingIdAtom } from '@/shared-ui';
import type { GetProjectQuery } from '@/survey-graphql';

import type { UsabilityResponseSession } from './project-usability-response-session.hooks';
import { useUsabilitySpeechDetection } from './project-usability-speech-detection.hooks';

interface UsabilityResponseVoiceOptions extends Pick<
  UsabilityResponseSession,
  | 'isConversationOver'
  | 'isRecording'
  | 'isPaused'
  | 'isPausedByTimeout'
  | 'isSubmitting'
> {
  project: Pick<GetProjectQuery['project'], 'isAiVoiceEnabled'>;
}

export interface UsabilityResponseVoice {
  currentPlayingId: string | null;
  ttsHasDrained: boolean;
  showPrompt: boolean;
  isFadingOut: boolean;
  onSpeechPromptClose: () => void;
}

export function useUsabilityResponseVoice({
  project,
  isConversationOver,
  isRecording,
  isPaused,
  isPausedByTimeout,
  isSubmitting,
}: UsabilityResponseVoiceOptions): UsabilityResponseVoice {
  const currentPlayingId = useAtomValue(currentPlayingIdAtom);
  const audioQueue = useAtomValue(audioQueueAtom);

  // Gate the wrapping-up overlay on the closing TTS finishing (not just
  // isConversationOver), so with AI voice the overlay doesn't cover the final
  // message while it's still being spoken.
  const isSpeaking = currentPlayingId !== null;
  const hasPendingAudio = audioQueue.some(
    (i) =>
      i.status === 'pending' ||
      i.status === 'loading' ||
      i.status === 'ready' ||
      i.status === 'playing'
  );
  const ttsHasDrained =
    isConversationOver &&
    (!(project?.isAiVoiceEnabled ?? false) ||
      (!isSpeaking && !hasPendingAudio));

  // Suppress the prompt when the session is paused (user pause or 5-min inactivity
  // timeout) — the mic stream stays alive during pause, so without this guard the
  // silence timer keeps running and fires the prompt over a paused session.
  const isRecordingActive = isRecording && !isPaused && !isPausedByTimeout;
  const {
    showPrompt,
    isFadingOut,
    onClose: onSpeechPromptClose,
  } = useUsabilitySpeechDetection({
    isRecording: isRecordingActive,
    isSubmitting,
  });

  return {
    currentPlayingId,
    ttsHasDrained,
    showPrompt,
    isFadingOut,
    onSpeechPromptClose,
  };
}
